import { useEffect, useState, RefObject } from 'react';
import { useLocation } from 'react-router-dom';
import {
  fetchCentralAiSnapshots,
  fetchCentralAiStatus,
  pingCentralAiPresence,
  centralAiHasActiveMonitoringTargets,
} from '../services/centralAiMonitorApi';
import { isShortPoolCctvPopupPath } from '../components/monitoring/cctvPopupRoutes';
import { MONITORING_POPUP_BBOX_LS_KEY } from '../components/monitoring/monitoringPopupBboxScript';
import { useAuth } from '../contexts/AuthContext';
import { useMonitoringAiSettings } from './useMonitoringAiSettings';
import { isHttp401 } from '../utils/monitoringAuthRedirect';

export const MONITORING_POPUP_SNAPSHOTS_MESSAGE = 'monitoring-popup-snapshots';

function readPopupBboxPref(): boolean {
  try {
    const v = localStorage.getItem(MONITORING_POPUP_BBOX_LS_KEY);
    return v === '1' || v === 'true';
  } catch {
    return false;
  }
}

/** srcDoc iframe은 API 오리진이 다를 수 있어, 부모(React)에서 스냅샷을 postMessage로 전달.
 *  「검출 박스 표시」ON 일 때만 폴링합니다.
 */
export function useMonitoringPopupSnapshotBridge(
  iframeRef: RefObject<HTMLIFrameElement | null>,
  enabled: boolean,
  operatingPoolIds: number[] = []
) {
  const { authState } = useAuth();
  const location = useLocation();
  const onPoolCctvPopup = isShortPoolCctvPopupPath(location.pathname);
  const opsReady =
    authState.isAuthenticated &&
    !authState.loading &&
    (authState.isAdminPasswordVerified || onPoolCctvPopup);
  const { settings } = useMonitoringAiSettings(opsReady);
  const operatingSetKey = operatingPoolIds.join(',');
  const [bboxPrefOn, setBboxPrefOn] = useState(readPopupBboxPref);

  useEffect(() => {
    const onStorage = (ev: StorageEvent) => {
      if (ev.key === MONITORING_POPUP_BBOX_LS_KEY || ev.key === null) {
        setBboxPrefOn(readPopupBboxPref());
      }
    };
    const onMessage = (ev: MessageEvent) => {
      if (ev.data?.type === 'monitoring-popup-bbox-pref') {
        setBboxPrefOn(Boolean(ev.data.on));
      }
    };
    window.addEventListener('storage', onStorage);
    window.addEventListener('message', onMessage);
    return () => {
      window.removeEventListener('storage', onStorage);
      window.removeEventListener('message', onMessage);
    };
  }, []);

  useEffect(() => {
    if (!enabled || !opsReady || !bboxPrefOn) return undefined;

    let cancelled = false;
    let intervalId: number | null = null;
    let bootId: number | null = null;

    const stopPolling = () => {
      cancelled = true;
      if (bootId != null) window.clearTimeout(bootId);
      if (intervalId != null) window.clearInterval(intervalId);
      bootId = null;
      intervalId = null;
    };

    const operatingSet = new Set(
      operatingPoolIds.map((id) => Number(id)).filter((n) => Number.isFinite(n))
    );

    const send = async () => {
      const win = iframeRef.current?.contentWindow;
      if (!win || cancelled) return;
      if (!localStorage.getItem('token')) return;
      if (!readPopupBboxPref()) return;
      try {
        const st = await fetchCentralAiStatus();
        if (!st.server_ai_enabled) return;
        const optIn = settings.central_monitor_opt_in !== false;
        const localTargets =
          optIn && settings.included_pool_ids.length > 0;
        if (
          !centralAiHasActiveMonitoringTargets(st) &&
          !localTargets &&
          !onPoolCctvPopup
        ) {
          return;
        }
        // 스냅샷 폴링 중에도 presence 유지 (없으면 서버가 detections를 비움)
        await pingCentralAiPresence();
        const rows = await fetchCentralAiSnapshots();
        if (cancelled) return;
        const filtered =
          operatingSet.size === 0
            ? rows
            : rows.filter((s) => {
                const pid = Number(s.pool_id);
                return Number.isFinite(pid) && operatingSet.has(pid);
              });
        win.postMessage(
          { type: MONITORING_POPUP_SNAPSHOTS_MESSAGE, rows: filtered },
          '*'
        );
      } catch (err) {
        if (isHttp401(err)) stopPolling();
      }
    };

    void send();
    bootId = window.setTimeout(() => void send(), 400);
    intervalId = window.setInterval(() => void send(), 900);
    return () => {
      stopPolling();
    };
  }, [
    enabled,
    opsReady,
    bboxPrefOn,
    iframeRef,
    operatingSetKey,
    onPoolCctvPopup,
    settings.server_ai_enabled,
    settings.central_monitor_opt_in,
    settings.included_pool_ids.join(','),
  ]);
}
